/**
 * Icon Utilities for MapComponent
 *
 * Provides utilities for converting Lucide React icons to HTMLImageElements
 * that can be used with MapLibre for custom markers and pins.
 */

import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Droplet,
  AlertTriangle,
  Info,
  Home,
  Building,
  Warehouse,
  Droplets,
  Thermometer,
  Zap,
  Wifi,
  Wrench,
  Camera,
  Activity,
  Gauge,
  Waves,
  CloudRain,
  FlaskConical,
  Wind,
  Sun,
  RadioTower,
  Flame,
  type LucideIcon,
} from 'lucide-react';

/**
 * Available pin icon names for the map component.
 * Maps to Lucide React icons.
 */
export type PinIconName =
  | 'pin'
  | 'camera'
  | 'sensor'
  | 'power'
  | 'home'
  | 'building'
  | 'warehouse'
  | 'water'
  | 'waves'
  | 'rain'
  | 'flask'
  | 'temperature'
  | 'wind'
  | 'sun'
  | 'wifi'
  | 'radio'
  | 'flame'
  | 'alert'
  | 'maintenance'
  | 'gauge'
  | 'info';

/**
 * Maps pin icon names to their corresponding Lucide icon components.
 */
export const PIN_ICON_MAP: Record<PinIconName, LucideIcon> = {
  pin: Droplet,
  camera: Camera,
  sensor: Activity,
  power: Zap,
  home: Home,
  building: Building,
  warehouse: Warehouse,
  water: Droplets,
  waves: Waves,
  rain: CloudRain,
  flask: FlaskConical,
  temperature: Thermometer,
  wind: Wind,
  sun: Sun,
  wifi: Wifi,
  radio: RadioTower,
  flame: Flame,
  alert: AlertTriangle,
  maintenance: Wrench,
  gauge: Gauge,
  info: Info,
};

/**
 * Default icon options for rendering.
 */
export interface IconRenderOptions {
  /** Icon size in pixels (default: 24) */
  size?: number;
  /** Icon color as CSS color string (default: '#1F2937') */
  color?: string;
  /** Stroke width (default: 2) */
  strokeWidth?: number;
}

const DEFAULT_ICON_OPTIONS: Required<IconRenderOptions> = {
  size: 24,
  color: '#1F2937',
  strokeWidth: 2,
};

/**
 * Converts a Lucide React icon to an HTMLImageElement for use with MapLibre.
 *
 * This function renders the icon to SVG, converts it to a data URL,
 * and returns a Promise that resolves to an HTMLImageElement.
 *
 * @param icon - The Lucide icon component to convert
 * @param options - Rendering options (size, color, strokeWidth)
 * @returns Promise resolving to an HTMLImageElement
 *
 * @example
 * ```typescript
 * import { MapPin } from 'lucide-react';
 *
 * const image = await lucideIconToImage(MapPin, { size: 32, color: '#10B981' });
 * map.addImage('custom-pin', image);
 * ```
 */
export async function lucideIconToImage(
  icon: LucideIcon,
  options: IconRenderOptions = {}
): Promise<HTMLImageElement> {
  const { size, color, strokeWidth } = { ...DEFAULT_ICON_OPTIONS, ...options };

  // Create a temporary container for rendering
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    // Create and render the icon using React 18's createRoot
    const root = createRoot(container);

    // Create the icon element with specified props
    const iconElement = createElement(icon, {
      size,
      color,
      strokeWidth,
    });

    // Use flushSync to force synchronous rendering so the SVG is in the DOM immediately
    flushSync(() => {
      root.render(iconElement);
    });

    // Get the SVG element
    const svg = container.querySelector('svg');
    if (!svg) {
      throw new Error('Failed to render icon: SVG element not found');
    }

    // Serialize SVG to string
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    // Convert to data URL
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

    // Create and load the image
    const image = new Image(size, size);

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load icon image'));
      image.src = dataUrl;
    });

    // Cleanup React root
    root.unmount();

    return image;
  } finally {
    // Always cleanup the temporary container
    document.body.removeChild(container);
  }
}

/**
 * Converts a pin icon name to an HTMLImageElement for use with MapLibre.
 *
 * @param iconName - The name of the pin icon (from PinIconName)
 * @param options - Rendering options (size, color, strokeWidth)
 * @returns Promise resolving to an HTMLImageElement
 *
 * @example
 * ```typescript
 * const image = await pinIconToImage('alert', { size: 28, color: '#EF4444' });
 * map.addImage('alert-pin', image);
 * ```
 */
export async function pinIconToImage(
  iconName: PinIconName,
  options: IconRenderOptions = {}
): Promise<HTMLImageElement> {
  const icon = PIN_ICON_MAP[iconName];
  if (!icon) {
    console.warn(`Unknown pin icon name: ${iconName}, falling back to 'pin'`);
    return lucideIconToImage(PIN_ICON_MAP.pin, options);
  }
  return lucideIconToImage(icon, options);
}

/**
 * Preloads multiple pin icons and returns them as a map.
 * Useful for initializing all required icons when the map loads.
 *
 * @param iconNames - Array of icon names to preload
 * @param options - Rendering options applied to all icons
 * @returns Promise resolving to a Map of icon names to HTMLImageElements
 *
 * @example
 * ```typescript
 * const icons = await preloadPinIcons(['pin', 'camera', 'alert'], { size: 24 });
 * icons.forEach((image, name) => map.addImage(`${name}-icon`, image));
 * ```
 */
export async function preloadPinIcons(
  iconNames: PinIconName[],
  options: IconRenderOptions = {}
): Promise<Map<PinIconName, HTMLImageElement>> {
  const results = new Map<PinIconName, HTMLImageElement>();

  // Load all icons in parallel
  const loadPromises = iconNames.map(async (name) => {
    const image = await pinIconToImage(name, options);
    results.set(name, image);
  });

  await Promise.all(loadPromises);

  return results;
}

/**
 * Gets all available pin icon names.
 *
 * @returns Array of all available pin icon names
 */
export function getAvailablePinIcons(): PinIconName[] {
  return Object.keys(PIN_ICON_MAP) as PinIconName[];
}

// ============================================================================
// Teardrop Marker Generation
// ============================================================================

/**
 * Normalize a CSS hex color string to a lowercase 6-digit hex (no #).
 * Handles 3-digit shorthand (e.g. "#abc" → "aabbcc").
 */
export function normalizeColorForImageId(color: string): string {
  let hex = color.replace('#', '').toLowerCase();
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  return hex;
}

/**
 * Extract the inner SVG content (paths, circles, lines) of a Lucide icon
 * as a serialized XML string with overridden stroke/fill.
 *
 * Uses renderToStaticMarkup (not createRoot + flushSync) so it's safe to
 * call from inside React lifecycle methods (useEffect, etc.).
 */
function extractLucideIconSVGContent(
  icon: LucideIcon,
  size: number = 24
): string {
  const iconElement = createElement(icon, {
    size,
    color: 'white',
    strokeWidth: 2,
  });

  // Render to static HTML string — no DOM needed, no flushSync
  const svgMarkup = renderToStaticMarkup(iconElement);

  // Parse the markup to extract inner SVG children
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) {
    throw new Error('Failed to render icon: SVG element not found in static markup');
  }

  const serializer = new XMLSerializer();
  let innerContent = '';
  for (let i = 0; i < svg.children.length; i++) {
    const child = svg.children[i].cloneNode(true) as Element;
    child.setAttribute('stroke', 'white');
    child.setAttribute('fill', 'none');
    child.setAttribute('stroke-width', '2');
    innerContent += serializer.serializeToString(child);
  }

  return innerContent;
}

/**
 * Generate a composite teardrop marker SVG string.
 * Colored teardrop shape + white Lucide icon centered in the head.
 */
function generateTeardropMarkerSVG(
  iconSvgInner: string,
  color: string
): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 66" width="100" height="132">
  <defs>
    <filter id="ds" x="-20%" y="-10%" width="140%" height="130%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)" />
    </filter>
  </defs>
  <path d="M25 3 C36 3 45 12 45 23 C45 34 25 61 25 61 C25 61 5 34 5 23 C5 12 14 3 25 3 Z"
    fill="${color}" stroke="rgba(0,0,0,0.2)" stroke-width="1.5" filter="url(#ds)" />
  <g transform="translate(13, 8)">
    ${iconSvgInner}
  </g>
</svg>`;
}

/**
 * Generate a single teardrop marker image for a given icon + color pair.
 * Returns the image name and loaded HTMLImageElement.
 */
export async function generateTeardropMarkerImage(
  iconName: PinIconName,
  color: string
): Promise<{ name: string; image: HTMLImageElement }> {
  const icon = PIN_ICON_MAP[iconName] || PIN_ICON_MAP.pin;
  const iconInner = extractLucideIconSVGContent(icon, 24);
  const svgString = generateTeardropMarkerSVG(iconInner, color);
  const colorHex = normalizeColorForImageId(color);
  const name = `pin-marker-${iconName}-${colorHex}`;

  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  const image = new Image(100, 132);

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error(`Failed to load teardrop marker: ${name}`));
    image.src = dataUrl;
  });

  return { name, image };
}

/**
 * Preload teardrop markers for all unique (icon, color) pairs.
 * Deduplicates by normalized icon+color key.
 */
export async function preloadTeardropMarkers(
  markerSpecs: Array<{ iconName: PinIconName; color: string }>
): Promise<Array<{ name: string; image: HTMLImageElement }>> {
  // Deduplicate
  const seen = new Set<string>();
  const unique: Array<{ iconName: PinIconName; color: string }> = [];

  for (const spec of markerSpecs) {
    const key = `${spec.iconName}-${normalizeColorForImageId(spec.color)}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(spec);
    }
  }

  return Promise.all(
    unique.map(({ iconName, color }) => generateTeardropMarkerImage(iconName, color))
  );
}

export default {
  lucideIconToImage,
  pinIconToImage,
  preloadPinIcons,
  getAvailablePinIcons,
  normalizeColorForImageId,
  generateTeardropMarkerImage,
  preloadTeardropMarkers,
  PIN_ICON_MAP,
};
